import angular from 'angular';

export default angular
  .module('csm.ui.dateRangeSelector', [])
  .directive('dateRangeSelector', dateRangeSelectorDirective)
  .name;

dateRangeSelectorDirective.$inject = [];
  
/*******************************************************************************
 * dateRangeSelector */
/**
 * Date Range ComboBox. Provides selection of a date range from a list of range
 * options. <b><c>startDate</c></b> and <b><c>endDate</b></c> properties are
 * updated as the selection changes.
 *
 * <p>The list of date ranges available is determined by the property settings.
 * This is the full list of available date ranges:</p>
 * <pre>
 *   All Dates
 *   Today                             This Tax Quarter-To-Date
 *   This Week                         This Tax Year
 *   This Week-To-Date                 This Tax Year-To-Date
 *   This Month                        Yesterday
 *   This Month-To-Date                Last Week
 *   This [Fiscal] Quarter             Last Month
 *   This [Fiscal] Quarter-To-Date     Last [Fiscal] Quarter
 *   This [Fiscal] Year                Last [Fiscal] Year
 *   This [Fiscal] Year-To-Date        Last Tax Quarter
 *   This Tax Quarter                  Last Tax Year
 * </pre>
 *
 * <p><b>Fiscal/Tax Year</b></p>
 *
 * <p>The <b>useFiscalYear</b> property modifies the list of date ranges to
 * include Fiscal Year dates instead of calendar year dates. When <b>true</b>,
 * <b>Quarter|Year</b> and <b>Quarter|Year-To-Date</b> ranges are changed to
 * <b>Fiscal Quarter|Year</b> and <b>Fiscal Quarter|Year-To-Date</b> ranges
 * based on <b>fyStartMonth</b> and <b>fyStartDay</b>.</p>
 *
 * <p>The <b>useTaxYear</b> property works much like <i>useFiscalYear</i>. The
 * list of date ranges is modified to include <b>Tax Quarter</b> and <b>Tax Year</b>
 * ranges based on <b>tyStartMonth</b> and <b>tyStartDay</b>. However, <b>this
 * property is ignored if <i>useFiscalYear</i> is <code>false</code></b>. In
 * addition, if the <i>tyStartMonth</i> and <i>tyStartDay</i> properties are
 * the same as the Fiscal Year properties, the Tax Year date range options are
 * not included in the list of date ranges.</p>
 *
 * @ngdoc     directive
 * @name      dateRangeSelector
 * @memberof  csm.controls
 * @restrict  element
 *
 * @attr  {string}  defaultRange
 *   Default Range selection. Corresponds to the actual text of the range
 *   selection. Default is first range in the list.
 *
 * @attr  {boolean}  excludeAllDates
 *   Exclude <b>All Dates</b> option flag.
 *
 * @attr  {boolean}  excludeDayOptions
 *   Exclude <b>Today</b> and <b>Yesterday</b> options flag.
 *
 * @attr  {boolean}  excludeWeekOptions
 *   Exclude <b>This Week</b> and <b>Last Week</b> options flag.
 *
 * @attr  {boolean}  excludeMonthOptions
 *   Exclude <b>This Month</b> and <b>Last Month</b> options flag.
 *
 * @attr  {boolean}  excludeQuarterOptions
 *   Exclude <b>This Quarter</b> and <b>Last Quarter</b> options flag.
 *
 * @attr  {boolean}  excludeYearOptions
 *   Exclude <b>This Year</b> and <b>Last Year</b> options flag.
 *
 * @attr  {boolean}  excludeThisOptions
 *   Exclude <b>This Week|Month|Quarter|Year</b> options flag. Excludes these as
 *   well as the <b>To-Date</b> options.
 *
 * @attr  {boolean}  excludeToDateOptions
 *   Exclude <b>To-Date</b> options flag. Excludes these options and sets the
 *   <b>endDate</b> value for the <b>This Week|Month|Quarter|Year</b> options
 *   to the current date.
 *
 * @attr  {integer}  fyStartDay
 *   Fiscal Year Start Day. Updates data provider and resets the selected range
 *   to the default if <b>useFiscalYear</b> is <b>true</b>. Default = 1.
 *
 * @attr  {integer}  fyStartMonth
 *   Fiscal Year Start Month. Updates data provider and resets the selected range
 *   to the default if <b>useFiscalYear</b> is <b>true</b>. Default = 1 (January).
 *
 * @attr  {string}   selectClass
 *   <c>class</c> attribute for combobox <c>select</c> control.
 *
 * @attr  {string}   today
 *   Current date from which all date ranges are calculated. Default = today's date.
 *
 * @attr  {integer}  tyStartDay
 *   Tax Year Start Day. Updates data provider and resets the selected range to
 *   the default if <b>useTaxYear</b> is <b>true</b>. Default = 1.
 *
 * @attr  {integer}  tyStartMonth
 *   Tax Year Start Month. Updates data provider and resets the selected range to
 *   the default if <b>useTaxYear</b> is <b>true</b>. Default = 1 (January).
 *
 * @attr  {boolean}  useFiscalYear
 *   Use Fiscal Year flag. Changes the <b>Quarter|Year</b> options to <b>Fiscal
 *   Quarter|Year</b> and sets the date ranges for these options based on the
 *   <b>fyStartMonth</b> and <b>fyStartDay</b>.
 *
 * @attr  {boolean}  useFullRange
 *   Use Full Range flag. Sets the <b>endDate</b> value for the
 *   <b>This Week|Month|Quarter|Year</b> options to the end of the period range.
 *   this option with <b>excludeToDateOptions</b> if the full range is desired.
 *
 * @attr  {boolean}  useTaxYear
 *   Use Tax Year flag.  Adds <b>Tax Quarter|Year</b> options and sets the date
 *   ranges for these options based on the <b>tyStartMonth</b> and <b>tyStartDay</b>.
 *
 *   <p>This attribute is ignored if <i>useFiscalYear</i> is <i>false</i> and no
 *   Tax Year options are added if the Tax Year start is the same as the Fiscal Year start.
 *
 * @property  {Date}  startDate
 *   Start date for the selected range. This value is changed as the selection changes.
 *
 * @property  {Date}  endDate
 *   End date for the selected range. This value is changed as the selection changes.
 *
 * @example
 *   <date-range-selector
 *     defaultRange          = "<i>first range in list</i>"
 *     excludeAllDates       = "false"
 *     excludeDayOptions     = "false"
 *     excludeWeekOptions    = "false"
 *     excludeMonthOptions   = "false"
 *     excludeQuarterOptions = "false"
 *     excludeYearOptions    = "false"
 *     excludeThisOptions    = "false"
 *     excludeToDateOptions  = "false"
 *     today                 = "<i>current date</i>"
 *     useFiscalYear         = "false"
 *     useFullRange          = "false"
 *     fyStartMonth          = "1"
 *     fyStartDay            = "1"
 *     useTaxYear            = "false"
 *     tyStartMonth          = "1"
 *     tyStartDay            = "1"
 *   </date-range-selector>
 *******************************************************************************/
/* @ngInject */
function dateRangeSelectorDirective() {
  /** MS per day.  */ var MSDAY = 1000 * 60 * 60 * 24;
  /** MS per week. */ var MSWEEK = MSDAY * 7;

  /*--------------------------------------------------*/
  /* periodDates                                      */
  /*                                                  */
  /* Returns the Quarter and Year start and end dates */
  /* for the current date given the reporting period  */
  /* start month and day. Object returned contains:   */
  /*                                                  */
  /*   thisQtrStart  : Current Quarter Start Date     */
  /*   thisQtrEnd    : Current Quarter End Date       */
  /*   thisYearStart : Current Year Start Date        */
  /*   thisYearEnd   : Current Year End Date          */
  /*   lastQtrStart  : Previous Quarter Start Date    */
  /*   lastQtrEnd    : Previous Quarter End Date      */
  /*   lastYearStart : Previous Year Start Date       */
  /*   lastYearEnd   : Previous Year End Date         */
  /*                                                  */
  /* pmonth  Reporting period start month.            */
  /* pday    Reporting period start day.              */
  /* today   Current date without time values.        */
  /*--------------------------------------------------*/
  var periodDates = function (pmonth, pday, today) {
    /*-------------------------------------------*/
    /* Set the reporting period year to last     */
    /* year if today is before the period start. */
    /*-------------------------------------------*/
    var pstart = new Date(today.getFullYear(), pmonth, pday);
    var pyear =
      today < pstart ? pstart.getFullYear() - 1 : pstart.getFullYear();

    /*--------------------*/
    /* Build date ranges. */
    /*--------------------*/
    var rv = {};
    rv.thisQtrStart = qtrStartDate(pmonth, pday, today);
    rv.thisQtrEnd = qtrEndDate(rv.thisQtrStart);
    rv.thisYearStart = new Date(pyear, pmonth, pday);
    rv.thisYearEnd = new Date(
      rv.thisYearStart.getFullYear() + 1,
      rv.thisYearStart.getMonth(),
      rv.thisYearStart.getDate() - 1
    );
    rv.lastQtrStart = qtrStartDate(
      pmonth,
      pday,
      new Date(
        rv.thisQtrStart.getFullYear(),
        rv.thisQtrStart.getMonth(),
        rv.thisQtrStart.getDate() - 1
      )
    );
    rv.lastQtrEnd = qtrEndDate(rv.lastQtrStart);
    rv.lastYearStart = new Date(pyear - 1, pmonth, pday);
    rv.lastYearEnd = new Date(
      rv.lastYearStart.getFullYear() + 1,
      rv.lastYearStart.getMonth(),
      rv.lastYearStart.getDate() - 1
    );

    return rv;
  };

  /*------------------------------------------------------*/
  /* qtrStartDate                                         */
  /*                                                      */
  /* Returns the start of the quarter for the current     */
  /* date given the reporting period start month and day. */
  /*                                                      */
  /* pmonth  Reporting period start month.                */
  /* pday    Reporting period start day.                  */
  /* today   Current date without time values.            */
  /*------------------------------------------------------*/
  var qtrStartDate = function (pmonth, pday, today) {
    /*-----------------------------------------*/
    /* Use actual month numbers for the calcs. */
    /*-----------------------------------------*/
    pmonth++;
    var cmonth = today.getMonth() + 1;

    /*-----------------------------------------------------------------*/
    /* Find the quarter number (0-3) for the current month. Use        */
    /* previous month if current day is less than fiscal start day.    */
    /*                                                                 */
    /* Note: "|0" returns quarter number as whole number. Without      */
    /* this, quarter number is fractional producing the wrong results. */
    /*-----------------------------------------------------------------*/
    var qmonth = today.getDate() >= pday ? cmonth : cmonth - 1;
    var tmonth = qmonth >= pmonth ? qmonth : qmonth + 12;
    var qnumber = ((tmonth - pmonth) / 3) | 0;

    /*-------------------------------------------------------*/
    /* Create quarter start date using the current reporting */
    /* period start and add quarter*3 to the months.         */
    /*-------------------------------------------------------*/
    var pdate = new Date(today.getFullYear(), pmonth - 1, pday);
    var qyear =
      today >= pdate ? today.getFullYear() : today.getFullYear() - 1;
    var qstart = new Date(qyear, pmonth - 1 + qnumber * 3, pday);

    return qstart;
  };

  /*----------------------------------------------------*/
  /* qtrEndDate                                         */
  /*                                                    */
  /* Returns the end of the quarter for the start date. */
  /*                                                    */
  /* start  Quarter Start Date                          */
  /*----------------------------------------------------*/
  var qtrEndDate = function (start) {
    return new Date(
      start.getFullYear(),
      start.getMonth() + 3,
      start.getDate() - 1
    );
  };

  /*--------------------------------------------------------*/
  /* updateDP                                               */
  /*                                                        */
  /* Builds the data provider based on the reporting period */
  /* start and end dates. Resets the current selection.     */
  /*--------------------------------------------------------*/
  var updateDP = function (scope, opts) {
    /*------------------------------------------------*/
    /* Set today's date from current date without any */
    /* time values and set the fiscal year to last    */
    /* year if today is before the fiscal year start. */
    /*------------------------------------------------*/
    var today = new Date(
      opts.today.getFullYear(),
      opts.today.getMonth(),
      opts.today.getDate()
    );
    var thisWeekStart = new Date(today.getTime() - today.getDay() * MSDAY);
    var thisWeekEnd = new Date(thisWeekStart.getTime() + 6 * MSDAY);
    var thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    var thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    var yesterday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - 1
    );
    var lastWeekStart = new Date(thisWeekStart.getTime() - MSWEEK);
    var lastWeekEnd = new Date(thisWeekEnd.getTime() - MSWEEK);
    var lastMonthEnd = new Date(
      thisMonthStart.getFullYear(),
      thisMonthStart.getMonth(),
      thisMonthStart.getDate() - 1
    );
    var lastMonthStart = new Date(
      lastMonthEnd.getFullYear(),
      lastMonthEnd.getMonth(),
      1
    );

    /*---------------------------------------*/
    /* Build reporting period quarter and    */
    /* year dates as well as Tax Year dates. */
    /*---------------------------------------*/
    var fy = opts.useFiscalYear
      ? periodDates(opts.fyStartMonth - 1, opts.fyStartDay, today)
      : periodDates(0, 1, today);
    var ty = opts.useTaxYear
      ? periodDates(opts.tyStartMonth - 1, opts.tyStartDay, today)
      : null;

    /*--------------------------*/
    /* Build the data provider. */
    /*--------------------------*/
    var day = !opts.excludeDayOptions;
    var week = !opts.excludeWeekOptions;
    var month = !opts.excludeMonthOptions;
    var qtr = !opts.excludeQuarterOptions;
    var year = !opts.excludeYearOptions;
    var all = !opts.excludeAllDates;
    var thiz = !opts.excludeThisOptions;
    var todate = thiz && !opts.excludeToDateOptions;
    var useFull = todate || opts.useFullRange;
    var tfiscal = opts.useFiscalYear ? 'This Fiscal ' : 'This ';
    var lfiscal = opts.useFiscalYear ? 'Last Fiscal ' : 'Last ';
    var tax =
      opts.useTaxYear &&
      (!opts.useFiscalYear ||
        opts.tyStartMonth != opts.fyStartMonth ||
        opts.tyStartDay != opts.fyStartDay);
    var taxOnly = opts.useTaxYear && !opts.useFiscalYear;

    var dp = [];
    var dpadd = function (t, sd, ed) {
      dp.push({ text: t, startDate: sd, endDate: ed });
    };

    if (all) dpadd('All Dates', null, today);
    if (day) dpadd('Today', today, today);
    if (week && thiz)
      dpadd('This Week', thisWeekStart, useFull ? thisWeekEnd : today);
    if (week && todate) dpadd('This Week-To-Date', thisWeekStart, today);
    if (month && thiz)
      dpadd('This Month', thisMonthStart, useFull ? thisMonthEnd : today);
    if (month && todate) dpadd('This Month-To-Date', thisMonthStart, today);
    if (qtr && thiz && !taxOnly)
      dpadd(
        tfiscal + 'Quarter',
        fy.thisQtrStart,
        useFull ? fy.thisQtrEnd : today
      );
    if (qtr && todate && !taxOnly)
      dpadd(tfiscal + 'Quarter-To-Date', fy.thisQtrStart, today);
    if (year && thiz && !taxOnly)
      dpadd(
        tfiscal + 'Year',
        fy.thisYearStart,
        useFull ? fy.thisYearEnd : today
      );
    if (year && todate && !taxOnly)
      dpadd(tfiscal + 'Year-To-Date', fy.thisYearStart, today);
    if (qtr && thiz && tax)
      dpadd(
        'This Tax Quarter',
        ty.thisQtrStart,
        useFull ? ty.thisQtrEnd : today
      );
    if (qtr && todate && tax)
      dpadd('This Tax Quarter-To-Date', ty.thisQtrStart, today);
    if (year && thiz && tax)
      dpadd(
        'This Tax Year',
        ty.thisYearStart,
        useFull ? ty.thisYearEnd : today
      );
    if (year && todate && tax)
      dpadd('This Tax Year-To-Date', ty.thisYearStart, today);
    if (day) dpadd('Yesterday', yesterday, yesterday);
    if (week) dpadd('Last Week', lastWeekStart, lastWeekEnd);
    if (month) dpadd('Last Month', lastMonthStart, lastMonthEnd);
    if (qtr && !taxOnly)
      dpadd(lfiscal + 'Quarter', fy.lastQtrStart, fy.lastQtrEnd);
    if (year && !taxOnly)
      dpadd(lfiscal + 'Year', fy.lastYearStart, fy.lastYearEnd);
    if (qtr && tax) dpadd('Last Tax Quarter', ty.lastQtrStart, ty.lastQtrEnd);
    if (year && tax) dpadd('Last Tax Year', ty.lastYearStart, ty.lastYearEnd);

    scope.dataprovider = dp;

    /*--------------------------------------------------*/
    /* Attempt to find the date option for the provided */
    /* defaultRange. Default to the first item.         */
    /*--------------------------------------------------*/
    if (opts.defaultRange) {
      for (var i = 0; i < scope.dataprovider.length - 1; i++)
        if (scope.dataprovider[i].text == opts.defaultRange) {
          scope.selectedItem(scope.dataprovider[i]);
          break;
        }
    } else scope.selectedItem(scope.dataprovider[0]);
  };

  /*****************************************************************************
   * Directive Object
   *****************************************************************************/
  return {
    restrict: 'E',
    scope: { startDate: '=', endDate: '=', selectClass: '@' },
    template:
      '<select ng-class="selectClass" ng-options="d as d.text for d in dataprovider" ng-model="selectedItem" ng-model-options="{getterSetter:true}"/>',
    link: function (scope, elem, attrs) {
      var opts = {}; // Options for data provider.
      var current = null; // Current selection.

      /*------------------------------------------------------------------*/
      /* If the today option has not been specified, use the current date */
      /*------------------------------------------------------------------*/
      if (!attrs.hasOwnProperty('today')) opts.today = new Date();

      /*-----------------------------------*/
      /* overrides the focus function to   */
      /* set focus on the select element   */
      /*-----------------------------------*/
      elem[0].focus = function () {
        elem[0].querySelector('select').focus();
      };

      /*--------------------------------------------------------*/
      /* Getter/Setter for the current date. The setter updates */
      /* the start and end date properties on the scope.        */
      /*--------------------------------------------------------*/
      scope.selectedItem = function (item) {
        if (!item) return current;

        current = item;
        scope.startDate = current.startDate;
        scope.endDate = current.endDate;
      };

      /*-------------------------------------*/
      /* Sugar for throwing an error when    */
      /* an attribute value is not a number. */
      /*-------------------------------------*/
      var checkNaN = function (num, attr, val, msg) {
        if (isNaN(val)) throw attr + "='" + val + "': " + msg;
        return num;
      };

      /*---------------------------------------------------------*/
      /* Update the dataprovider options when attributes change. */
      /*---------------------------------------------------------*/
      attrs.$observe('fyStartDay', function (val) {
        opts.fyStartDay = checkNaN(
          val ? parseInt(val) : 1,
          'fyStartDay',
          val,
          'not a valid day of the month'
        );
      });
      attrs.$observe('fyStartMonth', function (val) {
        opts.fyStartMonth = checkNaN(
          val ? parseInt(val) : 1,
          'fyStartMonth',
          val,
          'not a valid month number'
        );
      });
      attrs.$observe('tyStartDay', function (val) {
        opts.tyStartDay = checkNaN(
          val ? parseInt(val) : 1,
          'tyStartDay',
          val,
          'not a valid day of the month'
        );
      });
      attrs.$observe('tyStartMonth', function (val) {
        opts.tyStartMonth = checkNaN(
          val ? parseInt(val) : 1,
          'tyStartMonth',
          val,
          'not a valid month number'
        );
      });
      attrs.$observe('today', function (val) {
        checkNaN(
          (opts.today = val ? new Date(val) : new Date()).getTime(),
          'today',
          val,
          'not a valid date string'
        );
      });

      /*---------------------------------------------------------------------*/
      /* These options probably don't need to be watched as it is unlikely a */
      /* need will arise for these options to be set programmatically. In    */
      /* any case, AngularJS does not create a watcher unless the attributes */
      /* contains bound references ({{}}) so there is no harm in observing.  */
      /*---------------------------------------------------------------------*/
      attrs.$observe('defaultRange', function (val) {
        opts.defaultRange = val;
      });
      attrs.$observe('excludeAllDates', function (val) {
        opts.excludeAllDates = val == 'true';
      });
      attrs.$observe('excludeDayOptions', function (val) {
        opts.excludeDayOptions = val == 'true';
      });
      attrs.$observe('excludeWeekOptions', function (val) {
        opts.excludeWeekOptions = val == 'true';
      });
      attrs.$observe('excludeMonthOptions', function (val) {
        opts.excludeMonthOptions = val == 'true';
      });
      attrs.$observe('excludeQuarterOptions', function (val) {
        opts.excludeQuarterOptions = val == 'true';
      });
      attrs.$observe('excludeYearOptions', function (val) {
        opts.excludeYearOptions = val == 'true';
      });
      attrs.$observe('excludeThisOptions', function (val) {
        opts.excludeThisOptions = val == 'true';
      });
      attrs.$observe('excludeToDateOptions', function (val) {
        opts.excludeToDateOptions = val == 'true';
      });
      attrs.$observe('useFiscalYear', function (val) {
        opts.useFiscalYear = val == 'true';
      });
      attrs.$observe('useFullRange', function (val) {
        opts.useFullRange = val == 'true';
      });
      attrs.$observe('useTaxYear', function (val) {
        opts.useTaxYear = val == 'true';
      });

      /*---------------------------------------------------*/
      /* Update the data provider when any options change. */
      /*---------------------------------------------------*/
      scope.$watch(
        function () {
          return opts;
        },
        function () {
          updateDP(scope, opts);
        },
        true
      );
    }
  };
}
